
import { LOCAL_STORAGE_KEY } from '@/constants/common';
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import type { CityFormData, PrimaryDestination } from '@/types/city';
import type { SelectedPlace } from '@/types/wizard';
import { warn } from '@/utils/logger';

export interface TripFormData {
    destination: string;
    dates: string;
    collaborators: string[];
    collaboratorEmail?: string;
    collaboratorName?: string;
    collaboratorPermission?: string;
    travelerCount?: number;
    selectedTripTypes: string[];
    selectedBudget: string | null;
    selectedTravelWithTypes: string[];
    tripVibe: string;
    inspirationLinks: string[];
    savedTiktoks: string[];
    fromDate?: Date | string | null;
    toDate?: Date | string | null;
    days?: number;
    is_using_day_count?: boolean;
    city?: CityFormData | null;
    /** User-chosen destination (city, locality, or country). Source of truth for display and payload when set. */
    primary_destination?: PrimaryDestination | null;
    selectedPlaces?: SelectedPlace[];
    copyPasteText?: string;
    skipAI?: boolean;
    name?: string;
    tag_list?: string[];
    is_private?: boolean;
    /**
     * The creator changed the categories in the editor. Saves then send
     * `categories_source: 'user'`, without which the backend keeps auto-filled categories.
     */
    categoriesEditedByUser?: boolean;
}

export interface TripFormValue {
    formData: TripFormData;
    updateFormData: (updates: Partial<TripFormData>) => void;
    updateDestinationAndDates: (data: Partial<Pick<TripFormData, 'destination' | 'dates'>>) => void;
    updateCollaborators: (collaborators: string[]) => void;
    updatePreferences: (preferences: Partial<Pick<TripFormData, 'selectedTripTypes' | 'selectedBudget' | 'selectedTravelWithTypes'>>) => void;
    updateTripVibe: (vibe: string) => void;
    updateCollaboratorForm: (
        updates: Partial<{
            collaboratorEmail: string;
            collaboratorName: string;
            collaboratorPermission: string;
            travelerCount: number;
        }>
    ) => void;
    clearTripFormData: () => void;
    placeIdBlacklist: string[];
    setPlaceIdBlacklist: React.Dispatch<React.SetStateAction<string[]>>;
}

const TripFormContext = createContext<TripFormValue | undefined>(undefined);

export const useTripForm = (): TripFormValue => {
    const context = useContext(TripFormContext);
    if (!context) {
        throw new Error('useTripForm must be used within a TripFormProvider');
    }
    return context;
};

const DEFAULT_FORM_DATA: TripFormData = {
    destination: '',
    dates: '',
    collaborators: [],
    collaboratorEmail: '',
    collaboratorName: '',
    collaboratorPermission: '',
    travelerCount: 1,
    selectedTripTypes: [],
    selectedBudget: null,
    selectedTravelWithTypes: [],
    tripVibe: '',
    inspirationLinks: [],
    savedTiktoks: [],
    fromDate: null,
    toDate: null,
    days: 3,
    is_using_day_count: true,
    city: null,
    selectedPlaces: [],
};

export const TripFormProvider = ({ children }: { children: ReactNode }) => {
    const [formData, setFormData] = useState<TripFormData>(() => {
        if (typeof window === 'undefined') return DEFAULT_FORM_DATA;
        try {
            const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return { ...DEFAULT_FORM_DATA, ...parsed.formData };
            }
        } catch (e: unknown) {
            warn('Failed to load trip form data:', e);
        }
        return DEFAULT_FORM_DATA;
    });

    const [placeIdBlacklist, setPlaceIdBlacklist] = useState<string[]>(() => {
        if (typeof window === 'undefined') return [];
        try {
            const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (saved) return JSON.parse(saved).placeIdBlacklist || [];
        } catch {
            // Ignore malformed localStorage payload
        }
        return [];
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ formData, placeIdBlacklist }));
        } catch (e) {
            warn('Failed to save trip form data:', e);
        }
    }, [formData, placeIdBlacklist]);

    const updateFormData = useCallback((updates: Partial<TripFormData>) => {
        setFormData((prev) => ({ ...prev, ...updates }));
    }, []);

    const updateDestinationAndDates = useCallback(
        (data: Partial<TripFormData>) => {
            updateFormData({
                destination: data.destination !== undefined ? data.destination : formData.destination,
                dates: data.dates !== undefined ? data.dates : formData.dates,
            });
        },
        [updateFormData, formData.destination, formData.dates]
    );

    const updateCollaborators = useCallback(
        (collaborators: string[]) => {
            updateFormData({ collaborators });
        },
        [updateFormData]
    );

    const updatePreferences = useCallback(
        (preferences: Partial<Pick<TripFormData, 'selectedTripTypes' | 'selectedBudget' | 'selectedTravelWithTypes'>>) => {
            updateFormData(preferences as Partial<TripFormData>);
        },
        [updateFormData]
    );

    const updateTripVibe = useCallback(
        (tripVibe: string) => {
            updateFormData({ tripVibe });
        },
        [updateFormData]
    );

    const updateCollaboratorForm = useCallback(
        (
            updates: Partial<{
                collaboratorEmail: string;
                collaboratorName: string;
                collaboratorPermission: string;
                travelerCount: number;
            }>
        ) => {
            updateFormData(updates as Partial<TripFormData>);
        },
        [updateFormData]
    );

    const resetFormData = useCallback(() => {
        setFormData({ ...DEFAULT_FORM_DATA, is_using_day_count: undefined });
        setPlaceIdBlacklist([]);
    }, []);

    const clearTripFormData = useCallback(() => {
        if (typeof window !== 'undefined') {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
        }
        resetFormData();
    }, [resetFormData]);

    const value: TripFormValue = useMemo(
        () => ({
            formData,
            updateFormData,
            updateDestinationAndDates,
            updateCollaborators,
            updatePreferences,
            updateTripVibe,
            updateCollaboratorForm,
            clearTripFormData,
            placeIdBlacklist,
            setPlaceIdBlacklist,
        }),
        [
            formData,
            updateFormData,
            updateDestinationAndDates,
            updateCollaborators,
            updatePreferences,
            updateTripVibe,
            updateCollaboratorForm,
            clearTripFormData,
            placeIdBlacklist,
            setPlaceIdBlacklist,
        ]
    );

    return <TripFormContext.Provider value={value}>{children}</TripFormContext.Provider>;
};

export default TripFormContext;
